import { createGenerateClassName, createMuiTheme, jssPreset, MuiThemeProvider } from '@material-ui/core/styles';
import 'babel-polyfill';
import { create } from 'jss';
import React from 'react';
import { hot } from 'react-hot-loader';
import JssProvider from 'react-jss/lib/JssProvider';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';
import { PersistGate } from 'redux-persist/integration/react';

import './styles/fonts.scss';
import './styles/main.scss';

import { OidcAuthProvider } from '@shared/auth/auth-provider';
import { AbilityContextProvider } from 'common/ability/ability-context';
import { KreoColors } from 'common/enums/kreo-colors';
import { EnvironmentContextProvider } from 'common/environment/environment-context-provider';
import { I18nContextProvider } from 'common/i18n/i18n-context';
import { ThemeProvider } from './components/theme-provider';
import { AppRoutes } from './routes';
import { SpinnerPage } from './routes/spinner-page';
import { history, persistor, store } from './store';
import { Styled } from './styled';
import { GtmContext } from './units/analytics/gtm';

const theme = createMuiTheme({
  /* theme for v^1.x */
  palette: {
    primary: {
      main: `${KreoColors.gray7}`,
    },
    secondary: {
      main: `${KreoColors.gray9}`,
    },
  },
  typography: {
    useNextVariants: true,
    fontFamily: [
      'Quicksand', 'Helvetica', 'Arial', 'sans-serif',
    ].join(','),
    fontSize: 12,
  },
});

const generateClassName = createGenerateClassName();
const jss = create({
  ...jssPreset(),
  // We define a custom insertion point that JSS will look for injecting the styles in the DOM.
  insertionPoint: 'jss-insertion-point',
});

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider>
          <OidcAuthProvider>
            <EnvironmentContextProvider>
              <GtmContext>
                <JssProvider jss={jss} generateClassName={generateClassName}>
                  <PersistGate loading={<SpinnerPage />} persistor={persistor}>
                    <AbilityContextProvider>
                      <I18nContextProvider>
                        <ConnectedRouter history={history}>
                          <React.Fragment>
                            <AppRoutes />
                            <Styled.ModalRoot id='modal-root' />
                          </React.Fragment>
                        </ConnectedRouter>
                      </I18nContextProvider>
                    </AbilityContextProvider>
                  </PersistGate>
                </JssProvider>
              </GtmContext>
            </EnvironmentContextProvider>
          </OidcAuthProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </Provider>
  );
};


export default hot(module)(App);
